import { __decorate, __extends } from "tslib";
import { CustomElement, upgradeProperty, attachShadowTemplate, coerceNumber, coerceBoolean } from '@tyler-components-web/core';
import { tylIconKeyboardArrowLeft, tylIconKeyboardArrowRight } from '@tylertech/tyler-icons/standard';
import { TabComponent } from '../tab/tab';
import { IconButtonComponent } from '../../icon-button';
import { TAB_BAR_CONSTANTS } from './tab-bar-constants';
import { TabBarFoundation } from './tab-bar-foundation';
import { TabBarAdapter } from './tab-bar-adapter';
import { IconComponent, TylerIconRegistry } from '../../icon';
var template = require('./tab-bar.html');
var styles = require('./tab-bar.css');
/**
 * The custom element class behind the `<tcw-tab-bar>` element.
 */
var TabBarComponent = /** @class */ (function (_super) {
    __extends(TabBarComponent, _super);
    function TabBarComponent() {
        var _this = _super.call(this) || this;
        TylerIconRegistry.define([tylIconKeyboardArrowLeft, tylIconKeyboardArrowRight]);
        attachShadowTemplate(_this, TAB_BAR_CONSTANTS.elementName, template, styles);
        _this._foundation = new TabBarFoundation(new TabBarAdapter(_this));
        return _this;
    }
    Object.defineProperty(TabBarComponent, "observedAttributes", {
        get: function () {
            return [
                TAB_BAR_CONSTANTS.attributes.ACTIVE_TAB,
                TAB_BAR_CONSTANTS.attributes.LAYOUT_MODE,
                TAB_BAR_CONSTANTS.attributes.LAYOUT_ALIGN,
                TAB_BAR_CONSTANTS.attributes.UNDERLINE,
                TAB_BAR_CONSTANTS.attributes.FOCUS_ON_ACTIVATE,
                TAB_BAR_CONSTANTS.attributes.AUTO_ACTIVATE,
                TAB_BAR_CONSTANTS.attributes.STACKED,
                TAB_BAR_CONSTANTS.attributes.SCROLL_BUTTONS,
                TAB_BAR_CONSTANTS.attributes.FORCE_SCROLL_BUTTONS
            ];
        },
        enumerable: true,
        configurable: true
    });
    TabBarComponent.prototype.initializedCallback = function () {
        upgradeProperty(this, 'activeTab');
        upgradeProperty(this, 'layoutMode');
        upgradeProperty(this, 'layoutAlign');
        upgradeProperty(this, 'underline');
        upgradeProperty(this, 'focusOnActivate');
        upgradeProperty(this, 'autoActivate');
        upgradeProperty(this, 'stacked');
        upgradeProperty(this, 'scrollButtons');
        upgradeProperty(this, 'forceScrollButtons');
    };
    TabBarComponent.prototype.connectedCallback = function () {
        this._foundation.initialize();
    };
    TabBarComponent.prototype.disconnectedCallback = function () {
        this._foundation.disconnect();
    };
    TabBarComponent.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
        switch (name) {
            case TAB_BAR_CONSTANTS.attributes.ACTIVE_TAB:
                this.activeTab = coerceNumber(newValue);
                break;
            case TAB_BAR_CONSTANTS.attributes.LAYOUT_MODE:
                this.layoutMode = newValue;
                break;
            case TAB_BAR_CONSTANTS.attributes.LAYOUT_ALIGN:
                this.layoutAlign = newValue;
                break;
            case TAB_BAR_CONSTANTS.attributes.UNDERLINE:
                this.underline = this.hasAttribute(TAB_BAR_CONSTANTS.attributes.UNDERLINE);
                break;
            case TAB_BAR_CONSTANTS.attributes.FOCUS_ON_ACTIVATE:
                this.focusOnActivate = coerceBoolean(newValue);
                break;
            case TAB_BAR_CONSTANTS.attributes.AUTO_ACTIVATE:
                this.autoActivate = coerceBoolean(newValue);
                break;
            case TAB_BAR_CONSTANTS.attributes.STACKED:
                this.stacked = this.hasAttribute(TAB_BAR_CONSTANTS.attributes.STACKED);
                break;
            case TAB_BAR_CONSTANTS.attributes.SCROLL_BUTTONS:
                this.scrollButtons = coerceBoolean(newValue);
                break;
            case TAB_BAR_CONSTANTS.attributes.FORCE_SCROLL_BUTTONS:
                this.forceScrollButtons = this.hasAttribute(TAB_BAR_CONSTANTS.attributes.FORCE_SCROLL_BUTTONS);
                break;
        }
    };
    Object.defineProperty(TabBarComponent.prototype, "activeTab", {
        /** Gets and sets the active tab index. */
        get: function () {
            return this._foundation.activeTab;
        },
        set: function (value) {
            this._foundation.activeTab = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "layoutMode", {
        /** Gets/sets the layout mode that controls how the tabs are sized and rendered. */
        get: function () {
            return this._foundation.layoutMode;
        },
        set: function (value) {
            this._foundation.layoutMode = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "layoutAlign", {
        /** Gets/sets the layout alignment. Only pertains to non-full width layout modes. */
        get: function () {
            return this._foundation.layoutAlign;
        },
        set: function (value) {
            this._foundation.layoutAlign = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "underline", {
        /** Gets/sets whether the component displays an underline or not. Default is `false`. */
        get: function () {
            return this._foundation.underline;
        },
        set: function (value) {
            this._foundation.underline = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "focusOnActivate", {
        /** Gets/sets whether tabs receive focus automatically when activated. Default is `true`. */
        get: function () {
            return this._foundation.focusOnActivate;
        },
        set: function (value) {
            this._foundation.focusOnActivate = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "autoActivate", {
        /** Gets/sets whether tabs are auto-activated when using arrow keys. Default is `true` */
        get: function () {
            return this._foundation.autoActivate;
        },
        set: function (value) {
            this._foundation.autoActivate = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "stacked", {
        /** Gets/sets whether the tabs are displayed with as their stacked variant. Default is `false`. */
        get: function () {
            return this._foundation.stacked;
        },
        set: function (value) {
            this._foundation.stacked = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "scrollButtons", {
        /** Gets/sets whether the scroll buttons can be displayed or not. Default is `true`. The component handles visibility automatically. */
        get: function () {
            return this._foundation.scrollButtons;
        },
        set: function (value) {
            this._foundation.scrollButtons = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TabBarComponent.prototype, "forceScrollButtons", {
        /** Gets/sets whether the scroll buttons are visible indefinitely or not. Default is `false`. */
        get: function () {
            return this._foundation.forceScrollButtons;
        },
        set: function (value) {
            this._foundation.forceScrollButtons = value;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Activates the tab at the given index.
     * @param index The index of the tab.
     */
    TabBarComponent.prototype.activateTab = function (index) {
        this._foundation.activateTab(index);
    };
    /**
     * Scrolls the tab at the given index into view.
     * @param index The index of the tab.
     */
    TabBarComponent.prototype.scrollTabIntoView = function (index) {
        this._foundation.scrollIntoView(index);
    };
    TabBarComponent = __decorate([
        CustomElement({
            name: TAB_BAR_CONSTANTS.elementName,
            dependencies: [
                TabComponent,
                IconButtonComponent,
                IconComponent
            ]
        })
    ], TabBarComponent);
    return TabBarComponent;
}(HTMLElement));
export { TabBarComponent };
